<template>
    <div class="current-visitor">
        <Card size="small">
            <header>
                <div class="name">{{ $t('venue_statistics.current_visitor.headline') }}</div>
                <Select v-model="select" :options="options"></Select>
            </header>
            <div v-if="showEmptyState" class="empty-state">
                <p>
                    {{ $t('venue_statistics.current_visitor.empty_state') }}
                </p>
            </div>
            <div v-else class="chart-content">
                <DDoughnut
                    :items="chartData"
                    :colors="[
                        'rgba(3, 218, 197, 1)',
                        'rgba(255, 66, 126, 1)',
                        'rgba(254, 196, 0, 1)',
                        'rgba(231, 233, 237, 1)',
                    ]"
                    :radius="85"
                    :show-count="showCount"
                    :total-count="totalCount"
                />
                <div v-if="venueAnalytics" class="chart-legend">
                    <div class="item recurring">
                        <h2>
                            {{ occurrenceFormatted(venueAnalytics.visitors.recurring) }}
                        </h2>
                        <p>{{ $t('venue_statistics.current_visitor.recurring_visitors') }}</p>
                    </div>
                    <div class="item new">
                        <h2>
                            {{ occurrenceFormatted(venueAnalytics.visitors.new) }}
                        </h2>
                        <p>{{ $t('venue_statistics.current_visitor.new_visitors') }}</p>
                    </div>
                    <div v-if="!historicalData" class="item vip">
                        <h2>
                            {{ occurrenceFormatted(venue.manualCheckins) }}
                        </h2>
                        <p>{{ $t('venue_statistics.current_visitor.manual_checkins') }}</p>
                    </div>
                    <div v-else-if="historicalData" class="item vip">
                        <h2>
                            {{ occurrenceFormatted(venueAnalytics.visitors.vip) }}
                        </h2>
                        <p>{{ $t('venue_statistics.current_visitor.vip_visitors') }}</p>
                    </div>
                </div>
            </div>
        </Card>
    </div>
</template>

<script>
import Card from '@/components/Card'
import Select from '@/components/Select'
import gql from 'graphql-tag'
import DDoughnut from './DDoughnut'

export default {
    name: 'CurrentVisitor',
    components: {
        Card,
        Select,
        DDoughnut,
    },
    props: {
        venueAnalytics: {
            type: Object,
            default: undefined,
        },
        showCount: {
            type: Boolean,
            default: undefined,
        },
        historicalData: {
            type: Boolean,
            default: false,
        },
        totalCount: {
            type: Number,
            default: undefined,
        },
    },
    apollo: {
        venue: {
            query: gql`
                query ($id: ID!) {
                    venue(id: $id) {
                        id
                        capacity
                        manualCheckins
                    }
                }
            `,
            variables() {
                return {
                    id: this.$route.params.id,
                }
            },
        },
    },
    data() {
        return {
            options: [
                { value: 'percent', text: '%' },
                { value: 'sum', text: 'Sum' },
            ],
            select: 'sum',
            chart: null,
        }
    },
    computed: {
        showEmptyState() {
            if (this.$apollo.queries.venue.loading) return true
            if (this.historicalData) return false

            return !(
                this.venueAnalytics &&
                this.venueAnalytics.visitors &&
                (this.venueAnalytics.visitors.recurring > 0 ||
                    this.venueAnalytics.visitors.new > 0 ||
                    this.venue.manualCheckins > 0)
            )
        },
        chartData() {
            let data = [0, 0, 0]

            if (!(this.venueAnalytics || this.venue)) return data

            if (!this.historicalData) {
                const visitors = this.venueAnalytics.visitors
                const total = visitors.recurring + visitors.new + this.venue.manualCheckins
                this.totalCount = total

                data = [visitors.recurring, visitors.new, this.venue.manualCheckins]

                return data.map((el) => el / total)
            } else {
                const visitors = this.venueAnalytics.visitors
                const total = visitors.recurring + visitors.new + visitors.vip
                this.totalCount = total

                data = [visitors.recurring, visitors.new, visitors.vip]

                return data.map((el) => el / total)
            }
        },
    },
    methods: {
        occurrenceFormatted(occurrence) {
            if (this.select === 'percent') {
                if (this.venueAnalytics) {
                    if (!this.historicalData) {
                        const cV = this.venueAnalytics.visitors
                        const total = cV.new + cV.recurring + this.venue.manualCheckins
                        return `${Math.round((occurrence / total) * 100)} %`
                    } else {
                        const cV = this.venueAnalytics.visitors
                        const total = cV.new + cV.recurring + cV.vip
                        return `${Math.round((occurrence / total) * 100)} %`
                    }
                }
            }

            return occurrence
        },
    },
}
</script>

<style scoped lang="sass">
.current-visitor
    width: 100%

    .empty-state
        display: flex
        justify-content: center
        align-items: center
        height: 100px
        text-align: center
        user-select: none


    .card
        box-sizing: border-box
        min-height: 255px
        background: #ffffff !important

    header
        display: flex
        justify-content: space-between
        margin-bottom: $l

        .d-select
            min-width: 80px

        .name
            color: $neutral-black
            font-weight: 600
            padding: $xs
            font-size: 14px
            line-height: 17px

    .chart-content
        display: flex
        flex-wrap: wrap
        .chart-legend
            margin-left: 56px

            .item
                margin-bottom: 14px

                &.recurring h2
                    color: $green

                &.new h2
                    color: $red

                &.vip h2
                    color: $yellow

                h2
                    margin-bottom: 2px
                    font-weight: bold
                    font-size: 22px
                    line-height: 28px
                    letter-spacing: 0.5px

                p
                    color: $neutral-3

        .chart-container
            width: 170px
</style>
